import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { ArrowRight, FileCode2, LoaderCircle, LockKeyhole, Mail, Sparkles } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";
import { supabase } from "../lib/supabase";

const schema = z.object({ email: z.string().email("请输入有效邮箱"), password: z.string().min(6, "密码至少 6 位") });
type Values = z.infer<typeof schema>;

export function LoginPage({ authenticated = false, visualOnly = false }: { authenticated?: boolean; visualOnly?: boolean }) {
  const [mode, setMode] = useState<"login" | "signup">("login");
  const navigate = useNavigate();
  const location = useLocation();
  const form = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { email: "", password: "" } });
  const mutation = useMutation({
    mutationFn: async (values: Values) => {
      if (visualOnly) return;
      const result = mode === "login" ? await supabase.auth.signInWithPassword(values) : await supabase.auth.signUp(values);
      if (result.error) throw result.error;
    },
    onSuccess: () => {
      toast.success(mode === "login" ? "登录成功" : "注册成功，请检查验证邮件");
      if (mode === "login" && !visualOnly) {
        const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname || "/workspace";
        navigate(from, { replace: true });
      }
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "认证失败"),
  });

  if (authenticated && !visualOnly) return <Navigate to="/workspace" replace />;

  return (
    <main className="login-page" data-ui-capture="login-page">
      <section className="login-visual">
        <div className="login-brand"><span className="brand-icon large"><Sparkles size={24} /></span><div><strong>SVG Render</strong><small>Text diagram workspace</small></div></div>
        <div className="login-copy"><span className="kicker">PRIVATE RENDER PIPELINE</span><h1>从数学源码到清晰 SVG，统一在一个工作台完成。</h1><p>TikZ、PGFPlots、Graphviz 与 D2 共用私有上传、异步构建、实时进度和结果下载链路。</p></div>
        <div className="login-engines"><span>TikZ / PGF</span><span>PGFPlots</span><span>Graphviz</span><span>D2</span></div>
      </section>
      <section className="login-panel">
        <div className="login-card">
          <span className="login-file-icon"><FileCode2 size={20} /></span>
          <h2>{mode === "login" ? "登录工作台" : "创建账号"}</h2>
          <p>{mode === "login" ? "继续管理绘图任务和下载结果。" : "注册后即可使用私有异步渲染。"}</p>
          <form className="auth-form" onSubmit={form.handleSubmit((values) => mutation.mutate(values))}>
            <label><span>邮箱</span><div className="input-with-icon"><Mail size={16} /><input type="email" autoComplete="email" placeholder="name@example.com" {...form.register("email")} /></div><small className="field-error">{form.formState.errors.email?.message}</small></label>
            <label><span>密码</span><div className="input-with-icon"><LockKeyhole size={16} /><input type="password" autoComplete={mode === "login" ? "current-password" : "new-password"} placeholder="至少 6 位" {...form.register("password")} /></div><small className="field-error">{form.formState.errors.password?.message}</small></label>
            <button className="primary-button" disabled={mutation.isPending}>{mutation.isPending ? <LoaderCircle className="spin" size={17} /> : <ArrowRight size={17} />}{mode === "login" ? "登录" : "注册"}</button>
          </form>
          <button className="text-button auth-switch" onClick={() => setMode(mode === "login" ? "signup" : "login")}>{mode === "login" ? "没有账号？创建一个" : "已有账号？返回登录"}</button>
        </div>
      </section>
    </main>
  );
}
